import { useRef, useState } from 'react';
import Slider, { Settings } from 'react-slick';
import { Segmented } from 'antd';
import usePlatform from '../../hooks/usePlatform';
import { ossPrefix } from './constants';
import { isSSR } from '../../scripts/utils';
import './aip-carousel.scss';

const list = [
  {
    name: '制造商',
    pointLeft: `${845/1344*100}%`,
    pointTop: `${526/600*100}%`,
    src: `${ossPrefix}生产制造商.png`,
    tips: [
      { name: '有计划', desc: '伙伴协同计划步调一致' },
      { name: '控成本', desc: '成本分析控制及时准确' },
      { name: '高效率', desc: '生产过程管理规范高效' },
    ]
  },
  {
    name: '品牌商',
    pointLeft: `${970/1344*100}%`,
    pointTop: `${455/600*100}%`,
    src: `${ossPrefix}品牌商.png`,
    tips: [
      { name: '好品牌', desc: '品牌建设依赖统一数据' },
      { name: '多渠道', desc: '渠道发展兼顾效率规范' },
      { name: '控源头', desc: '把控核心原料供给品质' },
    ]
  },
  {
    name: '贸易商',
    pointLeft: `${1090/1344*100}%`,
    pointTop: `${385/600*100}%`,
    src: `${ossPrefix}渠道经销商.png`,
    tips: [
      { name: '高周转', desc: '提高库存周转减少积压' },
      { name: '增收益', desc: '管控价格成本提高收益' },
      { name: '控风险', desc: '管理资金以及账期风险' },
    ]
  },
]

// Tips组件
const TipsComponent = ({ tips }: { tips: Array<{ name: string; desc: string }> }) => {
  return (
    <div className="aip-carousel-tips">
      {tips.map((tip, index) => (
        <div key={index} className="aip-carousel-tip-item-wrapper">
          <div className="aip-carousel-tip-item">
            <img src="/images/aip/tip-done.png" className="aip-carousel-tip-icon" />
            <div className="aip-carousel-tip-content">
              <div className="aip-carousel-tip-name">{tip.name}:</div>
              <div className="aip-carousel-tip-desc">{tip.desc}</div>
            </div>
          </div>
          {index < tips.length - 1 && <div className="aip-carousel-tip-divider" />}
        </div>
      ))}
    </div>
  );
};

// PC 端定位点：32px 容器，12px 核心圆 + 32~40px 周期性扩散外圈，可点击切换对应信息
const PointComponent = ({
  pointLeft,
  pointTop,
  onClick,
}: {
  pointLeft: string;
  pointTop: string;
  onClick: () => void;
}) => (
  <>
    <div
      className="aip-carousel-point"
      style={{ left: pointLeft, top: pointTop }}
      role="button"
      tabIndex={0}
      onMouseMove={onClick}
    >
      <div className="aip-carousel-point-ring" />
      <div className="aip-carousel-point-core" />
    </div>
    <div
      onMouseMove={onClick}
      className="aip-carousel-point-cycle"
      style={{ left: pointLeft, top: pointTop, transform: `translate(-80%, -40%)` }}
    />
  </>
);

// 移动端静态图片（不再支持拖动）
const MobileImage = ({ src, name }: { src: string; name: string }) => (
  <div className="aip-carousel-mobile-image-container">
    <img src={src} alt={name} className="aip-carousel-mobile-image" />
  </div>
);

const SLIDER_SPEED_MS = 500;

export default function AIPCarousel() {
  const { isPc } = usePlatform() || {};
  const sliderRef = useRef<Slider | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const transitionLockRef = useRef(false);
  if (isSSR) {
    return null;
  }
  // PC端Slider配置
  const sliderSettings: Settings = {
    dots: false, // 禁用默认dots，我们自己渲染
    infinite: true,
    speed: SLIDER_SPEED_MS,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    arrows: false,
    fade: true,
    beforeChange: (current, next) => {
      setActiveIndex(next);
    },
  };

  const handleTabChange = (key: string) => {
    const index = parseInt(key);
    setActiveIndex(index);
  };

  const handlePointClick = (index: number) => {
    if (transitionLockRef.current) return;
    transitionLockRef.current = true;
    setActiveIndex(index);
    sliderRef.current?.slickGoTo(index);
    setTimeout(() => {
      transitionLockRef.current = false;
    }, SLIDER_SPEED_MS);
  };

  // PC端渲染
  if (isPc) {
    return (
      <div className="aip-carousel aip-carousel-pc">
        <div className="aip-carousel-pc-wrapper">
          <div className="aip-carousel-image-container">
            <Slider ref={sliderRef} {...sliderSettings} className="aip-carousel-slider">
              {list.map((item, index) => (
                <div key={index} className="aip-carousel-slide">
                  <div className="aip-carousel-image-wrapper">
                    <img src={item.src} alt={item.name} className="aip-carousel-image" />
                  </div>
                </div>
              ))}
            </Slider>
            <TipsComponent tips={list[activeIndex].tips} />
            {list
              .map((item, index) => {
                if (index === activeIndex) {
                  return null;
                }
                return (
                  <PointComponent
                    key={item.name}
                    pointLeft={item.pointLeft}
                    pointTop={item.pointTop}
                    onClick={() => handlePointClick(index)}
                  />
              )})}
            <div className="aip-carousel-dots">
              <ul>
                {list.map((_, index) => (
                  <li
                    key={index}
                    className={activeIndex === index ? 'slick-active' : ''}
                    onClick={() => handlePointClick(index)}
                  >
                    <div className="aip-carousel-dot" />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 移动端渲染：Segmented 在上，图片在下，图片不拖动
  return (
    <div className="aip-carousel aip-carousel-mobile">
      <div className="aip-carousel-mobile-wrapper">
        <div className="aip-carousel-mobile-segmented-wrapper">
          <Segmented
            value={activeIndex.toString()}
            onChange={(value) => handleTabChange(value as string)}
            options={list.map((item, index) => ({
              label: item.name,
              value: index.toString(),
            }))}
          />
        </div>
        <MobileImage src={list[activeIndex].src} name={list[activeIndex].name} />
      </div>
      <TipsComponent tips={list[activeIndex].tips} />
    </div>
  );
}